import { Indicator } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { Link, useMatches, useRouter } from '@tanstack/react-router';
import type React from 'react';
import { Suspense, use, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { ProjectContext } from '#core/contexts/ProjectContext.ts';

import { MenuLink } from './MenuLink.tsx';
import classes from './Sidebar.module.css';
import { UserMenu } from './UserMenu.tsx';

export function Sidebar() {
  const { config } = use(ProjectContext);
  const { routesByPath } = useRouter();
  const currentMatch = useMatches().at(-1);
  const portalRef = useRef<HTMLDivElement | null>(null);
  const LogoComponent = config?.logo;
  const sidebarEntries = Object.entries(routesByPath)
    .map(([path, route]) => [path, route.options.staticData?.route])
    .filter(([, route]) => route?.sidebarPosition !== undefined)
    .toSorted(([, a], [, b]) => a.sidebarPosition - b.sidebarPosition)
    .map(([path, { icon, title }]) => (
      <MenuLink
        from={currentMatch?.fullPath}
        icon={icon}
        key={path}
        params={currentMatch?.params}
        title={title}
        to={path}
      />
    ));

  return (
    <>
      <nav className={classes.sidebar}>
        <Link className={classes.logo} to="/">
          <LogoComponent />
        </Link>

        {sidebarEntries}

        <div className={classes.additionalEntries}>
          {customSidebarEntries.map(([name, Component]) => (
            <ErrorBoundary
              fallback={
                <Indicator color="red" label={<IconAlertCircle size={18} />} size={20}>
                  <div className={classes.entryError}>
                    <Component portalTarget={portalRef} />
                  </div>
                </Indicator>
              }
              key={name}
            >
              <Suspense
                fallback={
                  <div className={classes.entryLoading}>
                    <Component portalTarget={portalRef} />
                  </div>
                }
              >
                <Component portalTarget={portalRef} />
              </Suspense>
            </ErrorBoundary>
          ))}

          <UserMenu />
        </div>
      </nav>

      <div className={classes.portal} ref={portalRef} />
    </>
  );
}

export interface CustomSidebarEntryProps {
  portalTarget: React.RefObject<HTMLElement | null>;
}

const customSidebarEntries = Object.entries({
  ...import.meta.glob(['/src/components/sidebar/*.tsx', '../sidebar/*.tsx'], { eager: true }),
})
  .map(([path, module]): [string, object] | undefined => {
    const componentName = path?.match(/.*\/(.*)\.ts/)?.[1];
    if (!componentName || !module || typeof module !== 'object' || !(componentName in module)) {
      return undefined;
    }
    try {
      const component = (module as Record<string, React.FunctionComponent>)[componentName] as React.FunctionComponent;
      console.debug(`Registered sidebar component: '${componentName}'`);
      return [componentName, component];
    } catch (e) {
      console.error(`Error loading sidebar component '${componentName}':`, e);
      return undefined;
    }
  })
  .filter((it): it is [string, React.FunctionComponent<CustomSidebarEntryProps>] => it?.[1] !== undefined);
